import React from 'react';
import { t } from '../i18n';
import { fmtDay, fmtMoney } from '../lib/format';

// The client-facing PROJECT STATEMENT — one row per trade, each with its Joist
// invoice link, what was billed, what's been paid and what's still owed. This
// is the view Julian fills in with the homeowner sitting next to him, so it
// deliberately shows nothing about cost or profit.
export default function ProjectStatement({ project, onRecordPayment, onEditLine, onDeleteLine, isAdmin }) {
  if (!project) return null;
  const lines = project.lines || [];

  const Cell = ({ label, value, bg, color }) => (
    <div style={{ flex: 1, minWidth: 110, background: bg, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
      <div style={{ fontSize: 19, color }}>{fmtMoney(value)}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Cell label={t('pj.invoiced')} value={project.invoicedTotal} bg="#f1f5f9" color="var(--slate)" />
        <Cell label={t('pj.received')} value={project.receivedTotal} bg="var(--green-light)" color="var(--green)" />
        <Cell label={t('pj.owed')} value={project.owedByClient} bg="var(--red-light)" color="var(--red)" />
      </div>

      {project.credit > 0 && (
        <div className="alert alert-green">{t('pj.credit')} {fmtMoney(project.credit)}</div>
      )}

      {lines.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>{t('pj.noLines')}</div>
      ) : lines.map(l => (
        <div key={l._id} className="lead-card" style={{ cursor: 'default', borderLeft: `3px solid ${l.owed <= 0 ? 'var(--green)' : 'var(--red)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div>
              <div style={{ fontSize: 14 }}>{l.trade}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {l.invoiceDate && <>{fmtDay(l.invoiceDate)}</>}
                {l.invoiceNumber && <> · {t('pj.invoiceNo')} {l.invoiceNumber}</>}
              </div>
              {/* The scope, carried over from the estimate they agreed to. A
                  bill that says only "DRYWALL $850" is a bill people ring
                  about; one that lists what it bought is one they pay. */}
              {l.notes && (
                <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{l.notes}</div>
              )}
            </div>
            <span className={`tag ${l.owed <= 0 ? 'tag-green' : 'tag-red'}`}>
              {l.owed <= 0 ? t('pj.fullyPaid') : t('pj.dueBalance')}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', margin: '8px 0' }}>
            {[[t('pj.amount'), l.amount, 'var(--navy)'], [t('pj.paid'), l.paid, 'var(--green)'], [t('pj.balance'), l.owed, l.owed > 0 ? 'var(--red)' : 'var(--green)']].map(([label, value, color]) => (
              <div key={label} style={{ flex: 1, minWidth: 90 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
                <div style={{ fontSize: 15, color }}>{fmtMoney(value)}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {l.invoiceUrl && (
              <button className="btn btn-outline btn-sm" onClick={() => window.open(l.invoiceUrl, '_blank')}>{t('pj.viewInvoice')}</button>
            )}
            {isAdmin && onRecordPayment && l.owed > 0 && (
              <button className="btn btn-green btn-sm" onClick={() => onRecordPayment(l)}>Record Payment</button>
            )}
            {isAdmin && onEditLine && <button className="btn btn-outline btn-sm" onClick={() => onEditLine(l)}>Edit</button>}
            {isAdmin && onDeleteLine && <button className="btn btn-ghost btn-sm" onClick={() => onDeleteLine(l)}>Delete</button>}
          </div>
        </div>
      ))}

      {/* Payments received, the way the spreadsheet lists them across the top */}
      {project.payments?.length > 0 && (
        <>
          <div className="sec-label" style={{ marginTop: 16 }}>{t('pj.paymentsReceived')}</div>
          <div className="lead-card" style={{ cursor: 'default' }}>
            {project.payments.map(p => (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px dashed var(--border)' }}>
                <span style={{ color: 'var(--muted)', minWidth: 74 }}>{fmtDay(p.date)}</span>
                <span className="tag tag-slate">{p.method}</span>
                {p.reference && <span style={{ color: 'var(--muted)', fontSize: 11 }}>#{p.reference}</span>}
                <span style={{ marginLeft: 'auto', color: 'var(--green)' }}>{fmtMoney(p.amount)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
